import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, X, Sparkles } from 'lucide-react';
import InquiryFormModal from './InquiryFormModal';
import FaqAccordion from './FaqAccordion';

const STORAGE_KEY = 'gw_banner_dismissed_intro_pt_v1';

const ptFaqs = [
  {
    q: 'Are the sessions one-on-one?',
    a: 'Yes — these are private one-on-one sessions with Jill. Small-group options for friends & family are available on request.',
  },
  {
    q: 'I\'ve never done personal training before. Is that okay?',
    a: 'Absolutely. We begin where your body actually is and build from there — no experience needed. The whole point is to rebuild confidence in movement at a pace that feels right for you.',
  },
  {
    q: 'I have an injury or chronic pain. Can I still join?',
    a: 'Yes — this work is designed for exactly that. We focus on restoring movement, easing compensation patterns, and rebuilding a foundation safely before adding load.',
  },
  {
    q: 'Is this just exercise?',
    a: 'No. It\'s a whole-person approach — movement, breathing, posture, deep core, stress and recovery, and the gut-wellness connection — sequenced in the right order so your body can actually receive the work.',
  },
  {
    q: 'How many sessions is it, and how often do we meet?',
    a: 'It\'s a 10-session introductory journey. Scheduling is flexible and tailored to you, so we can find a rhythm that fits your life and supports steady progress.',
  },
  {
    q: 'How do I get started?',
    a: 'Tap "Reach Out to Book" below and share a few details. Jill will be in touch with availability and next steps. Introductory spaces are limited.',
  },
];

const AnnouncementBanner = () => {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  });
  const [expanded, setExpanded] = useState(false);
  const [inquiry, setInquiry] = useState(null);
  const openInquiry = () => setInquiry({ openedAt: Date.now() });

  const dismiss = (e) => {
    e?.stopPropagation();
    setDismissed(true);
    setExpanded(false);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch (err) { /* ignore */ }
  };

  if (dismissed) return null;

  return (
    <div className="relative bg-gradient-to-r from-terracotta via-rose-400 to-amber-400 text-white shadow-md z-30" data-testid="announcement-banner">
      {/* Header row */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 md:px-6 py-3 flex items-center justify-center gap-3 hover:bg-black/5 transition-colors"
        data-testid="banner-toggle"
      >
        <Sparkles size={16} className="text-white flex-shrink-0" />
        <span className="font-body text-sm md:text-base font-medium text-center">
          <span className="hidden sm:inline">Now Booking: </span>
          <span className="font-semibold">10-Session Introductory Personal Training Package</span>
          <span className="hidden md:inline"> — Restore, Rebuild & Repattern</span>
        </span>
        <ChevronDown
          size={18}
          className={`flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dismiss X */}
      <button
        onClick={dismiss}
        aria-label="Dismiss announcement"
        data-testid="banner-dismiss"
        className="absolute top-2 right-2 md:top-3 md:right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
      >
        <X size={14} />
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden bg-cream text-forest"
            data-testid="banner-content"
          >
            <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-12 space-y-8">
              {/* Title */}
              <div className="text-center">
                <p className="text-xs font-body text-terracotta uppercase tracking-[0.25em] mb-3">
                  A Private Invitation
                </p>
                <h2 className="font-heading text-3xl md:text-5xl text-forest mb-3 leading-tight">
                  Restore, Rebuild &amp; Repattern
                </h2>
                <p className="font-body text-stone-600 text-lg">
                  A 10-Session Introductory Personal Training Package with Jill
                </p>
              </div>

              {/* Intro */}
              <div className="font-body text-stone-700 space-y-4 leading-relaxed">
                <p>
                  I&apos;m opening a small number of private client spaces for a guided 10-session journey
                  designed to help you rebuild from the inside out.
                </p>
                <p>
                  For many years my focus has been Restorative Movement. It grew out of my own health
                  challenges, injuries, surgeries, setbacks, and extensive education. What I&apos;ve
                  discovered is simple:
                </p>
                <p className="font-heading text-xl text-forest italic border-l-4 border-terracotta pl-4">
                  The body wants to heal. It just needs the right conditions and the right sequence.
                </p>
                <p>
                  Many people are trying harder than ever to get healthy, but they&apos;re starting in
                  the wrong place. They&apos;re pushing through fatigue. Trying to exercise their way out
                  of stress. Returning to the gym only to end up stiffer, more exhausted, and wondering
                  why they feel worse instead of better.
                </p>
                <p>
                  Sometimes the issue isn&apos;t motivation. Sometimes things are simply out of order.
                </p>
              </div>

              {/* This program is for you if */}
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-200">
                <h3 className="font-heading text-2xl text-forest mb-4">This Program Is For You If…</h3>
                <ul className="space-y-2 font-body text-stone-700">
                  {[
                    'You\'re dealing with chronic pain, stiffness, or recurring injuries.',
                    'You\'re recovering from surgery, illness, burnout, chronic/stubborn weight, or a stressful period of life — and want to get WELL and FIT.',
                    'You want to get back into fitness but don\'t know where to begin.',
                    'You have a nagging shoulder, hip, neck, or back issue that never quite resolves.',
                    'Your energy, sleep, cravings, or mood seem to fall apart whenever you try to "get healthy."',
                    'You\'re moving through menopause and finding that exercise isn\'t producing the results you expected.',
                    'You feel disconnected from your body and want to rebuild confidence in movement.',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 bg-terracotta rounded-full mt-2.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Why start here */}
              <div className="font-body text-stone-700 space-y-4 leading-relaxed">
                <h3 className="font-heading text-2xl text-forest">Why Start Here?</h3>
                <p>
                  These days there&apos;s a huge emphasis on lifting weights. Weights can be wonderful.
                  But before adding external load, we need to ask an important question:
                </p>
                <p className="font-heading text-lg text-forest italic">
                  Is the body prepared to receive that load?
                </p>
                <p>
                  If our posture, breathing, movement patterns, stress response, energy systems, and
                  recovery capacity aren&apos;t functioning well, adding more stress to the system may
                  simply reinforce compensation patterns.
                </p>
                <p>
                  This isn&apos;t about avoiding strength training. It&apos;s about <em>preparing</em>
                  &nbsp;the body for strength. Think of it as rebuilding the foundations before adding
                  another floor to the house.
                </p>
              </div>

              {/* What we'll explore */}
              <div className="bg-forest/5 rounded-2xl p-6 md:p-8 border border-forest/10">
                <h3 className="font-heading text-2xl text-forest mb-2">What We&apos;ll Explore</h3>
                <p className="font-body text-stone-600 text-sm mb-5">
                  Every program is individualized, but your sessions may include:
                </p>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 font-body text-stone-700 text-sm">
                  {[
                    'Movement Assessment & the 9 Pillars of Wellness',
                    'Deep Core Activation & Diaphragm Repatterning',
                    'Restoring Shoulder, Pelvis & Spine communication',
                    'Fascial Line Activation & Whole-Body Connection',
                    'Pain Relief & Compensation Pattern Assessment',
                    'The Gut-Wellness Connection',
                    'Rebuilding Energy from the Ground Up',
                    'Joint Communication & Movement Repatterning',
                    'Natural & Instinctive Movement Principles',
                    'Posture, Breathing & Movement Integration',
                    'Restoring Healthy Joint Mechanics',
                    'Introduction to Holistic Personal Training',
                    'Understanding Stress, Recovery & Capacity',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-terracotta mt-0.5">✦</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* My approach */}
              <div className="font-body text-stone-700 space-y-4 leading-relaxed">
                <h3 className="font-heading text-2xl text-forest">My Approach</h3>
                <p>I believe in looking at the whole person.</p>
                <div className="flex flex-wrap gap-2">
                  {['Sleep', 'Stress', 'Nutrition', 'Movement', 'Breathing', 'Energy', 'Recovery', 'Life circumstances'].map((t) => (
                    <span key={t} className="px-3 py-1 rounded-full bg-white border border-stone-200 text-sm font-body">{t}</span>
                  ))}
                </div>
                <p>Everything affects everything.</p>
                <p>
                  Rather than forcing the body to perform where we wish it was, we begin where it
                  actually is. Then we build from there.
                </p>
              </div>

              {/* A Different Way Forward */}
              <div className="font-body text-stone-700 space-y-4 leading-relaxed">
                <h3 className="font-heading text-2xl text-forest">A Different Way Forward</h3>
                <p>You may be fit but struggling with a nagging injury.</p>
                <p>You might look healthy but feel exhausted.</p>
                <p>You might be recovering from a major life event that&apos;s taken a toll physically, emotionally, or both.</p>
                <p>Whatever your starting point, there is a way forward.</p>
                <p>
                  My goal is not simply to help you exercise. My goal is to help you understand your
                  body, rebuild trust in it, and create a foundation that supports long-term health,
                  strength, and resilience.
                </p>
                <p className="font-heading text-lg text-forest italic">
                  I&apos;ve done the hard work so you don&apos;t have to. Now I&apos;d love to help you
                  find your way back to feeling at home in your body again.
                </p>
              </div>

              {/* FAQ */}
              <div data-testid="pt-faq-section">
                <h3 className="font-heading text-2xl text-forest mb-4 text-center">Frequently Asked Questions</h3>
                <FaqAccordion items={ptFaqs} testIdPrefix="pt-faq" />
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-br from-terracotta/10 to-amber-100/40 rounded-2xl p-6 md:p-8 text-center border border-terracotta/20">
                <p className="font-body text-stone-700 mb-1">
                  Limited introductory spaces available.
                </p>
                <p className="font-body text-stone-600 text-sm mb-5">
                  Private one-on-one sessions available · Small group options for friends &amp; family on request.
                </p>
                <button
                  onClick={openInquiry}
                  className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-8 py-3 rounded-full hover:bg-forest-dark transition-colors text-base"
                  data-testid="banner-reach-out"
                >
                  Reach Out to Book
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* INQUIRY FORM MODAL */}
      <InquiryFormModal
        key={`pt-inquiry-${inquiry?.openedAt || 'closed'}`}
        isOpen={!!inquiry}
        onClose={() => setInquiry(null)}
        programName="10-Session Personal Training Package"
        source="announcement-banner"
      />
    </div>
  );
};

export default AnnouncementBanner;
